import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";

/* The service answers a real HTTP status with a { code, message } body — there
   is no status field inside the payload to test. axios rejects on 4xx/5xx, so
   every thunk below reads the rejection rather than inspecting a success body.
   The 409 from a refused status move ("illegal_transition") arrives here with
   the graph's own message, which is what the screens surface verbatim. */
const failed = (err, fallback) =>
  err.response?.data?.message || err.response?.data?.code || fallback;

/* ================= FETCH ALL ORDERS (ADMIN) ================= */
export const fetchOrders = createAsyncThunk(
  "orders/fetchOrders",
  async ({ page = 1, limit = 10, status = "", deliveryMode = "", q = "" } = {}, thunkAPI) => {
    try {
      /* A paginated list is one of the few payloads whose OWN top-level key is
         "data": { data: [...], pagination: {...} }. res.data is that envelope,
         so the reducer still reads payload.data / payload.pagination. */
      const params = new URLSearchParams({ page, limit, status, deliveryMode });
      /* The search goes to the SERVER, which looks through every order --
         number, bill, name, email, phone. Filtering the ten rows already on
         screen instead meant a customer rang about an order from last week,
         staff typed their phone number, and the portal said "no results" about
         an order it simply had not fetched. Two characters is the server's own
         floor for a search rather than a scan. */
      if (q && q.trim().length >= 2) params.set("q", q.trim());
      const res = await api.get(`/admin/orders?${params.toString()}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to fetch orders"));
    }
  }
);

/* ================= FETCH STATUS COUNTS (ADMIN) ================= */
export const fetchStatusCounts = createAsyncThunk(
  "orders/fetchStatusCounts",
  async ({ deliveryMode = "delivery", statuses = [], range } = {}, thunkAPI) => {
    try {
      /* ONE request for every chip. This used to fire a GET /orders?limit=1 per
         status and read pagination.total; /admin/orders/status-counts groups
         them server-side under the same scope, visibility and filters as the
         list, so a chip and the list it opens cannot disagree. Statuses that
         were asked for and have no orders come back as 0, not absent. */
      const params = new URLSearchParams();
      if (statuses.length) params.set("status", statuses.join(","));
      if (deliveryMode) params.set("deliveryMode", deliveryMode);
      // range="today" restricts the counts to the current store-timezone day
      // (the dashboard's "Today Orders"); omitted elsewhere for all-time totals.
      if (range) params.set("range", range);

      const res = await api.get(`/admin/orders/status-counts?${params.toString()}`);
      // { counts: { pending: 3, accepted: 1, ... }, total }
      return res.data.counts || {};
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to fetch status counts"));
    }
  }
);

/* ================= FETCH SINGLE ORDER (ADMIN) ================= */
export const fetchOrderDetails = createAsyncThunk(
  "orders/fetchOrderDetails",
  async (orderNo, thunkAPI) => {
    try {
      /* Addressed by orderNo. The route also matches legacy_id, so an old Mongo
         ObjectId still in a bookmarked URL resolves. The payload is five
         siblings at the top level — { order, customer, items, history,
         siblings } — not one flat order, and that whole envelope is what the
         detail screens read. */
      const res = await api.get(`/admin/orders/${orderNo}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to fetch order details"));
    }
  }
);

/* ================= UPDATE ORDER STATUS (ADMIN) ================= */
export const updateOrderStatus = createAsyncThunk(
  "orders/updateOrderStatus",
  async ({ orderNo, status, note }, thunkAPI) => {
    try {
      /* The only route that moves an order. It answers 409
         { code: "illegal_transition", message } when the graph refuses the
         move — surfaced as-is, never retried. */
      const res = await api.post(`/fulfilment/orders/${orderNo}/status`, {
        status,
        ...(note ? { note } : {}),
      });
      // { orderNo, from, to } — the move that happened, not the whole order.
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to update order status"));
    }
  }
);

/* ================= FETCH MY ORDERS (CUSTOMER TOKEN) ================= */
export const fetchMyOrders = createAsyncThunk(
  "orders/fetchMyOrders",
  async (_, thunkAPI) => {
    try {
      // The customer's own history — a bare array, scoped to the caller's
      // customer token (not the admin one the portal carries).
      const res = await api.get(`/customer/orders`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to fetch user orders"));
    }
  }
);

/* ================= FETCH CUSTOMER ORDERS (ADMIN) ================= */
export const fetchCustomerOrders = createAsyncThunk(
  "orders/fetchCustomerOrders",
  async (customerId, thunkAPI) => {
    try {
      // { customer, data: [...] } — the person and their history at this
      // branch, so an unknown customer (404) reads differently from one with
      // no orders here.
      const res = await api.get(`/admin/orders/customer/${customerId}`);
      return res.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(failed(err, "Failed to fetch customer orders"));
    }
  }
);

/* ================= SLICE ================= */
const orderSlice = createSlice({
  name: "orders",
  initialState: {
    orders:       [],
    myOrders:     [],
     customerOrders:  [],
    customerProfile: null,
    orderDetails: null,
    /* ── WHICH ANSWER BELONGS TO THIS SCREEN ──
       Both lists are polled every ten seconds AND refetched on every filter
       change, so two requests are often in the air at once and the network
       decides which lands last. Without these, opening order B while A's poll
       is still in flight repaints B's page with A's items and totals, and
       clicking New then Packing quickly leaves the Packing rows under the New
       chip. Each field holds what was asked for most recently; an answer to
       anything else is dropped rather than painted. */
    orderDetailsFor: null,
    ordersRequestId: null,
    loading:      false,
    updating:     false,
    error:        null,
    pagination:   {},
    statusCounts: {
      delivery: {},
      pickup:   {},
    },
  },
  reducers: {
    clearOrderError:   (state) => { state.error = null; },
    clearOrderDetails: (state) => { state.orderDetails = null; state.orderDetailsFor = null; },
  },
  extraReducers: (builder) => {
    builder

      /* ── FETCH ORDERS ── */
      .addCase(fetchOrders.pending, (state, action) => {
        state.loading = true;
        state.error   = null;
        state.ordersRequestId = action.meta.requestId;
      })
      .addCase(fetchOrders.fulfilled, (state, action) => {
        // Only the most recently REQUESTED list may paint. A slower earlier
        // request answering second would otherwise put the previous filter's
        // rows on the screen under the chip the user has just chosen.
        if (state.ordersRequestId && action.meta.requestId !== state.ordersRequestId) return;
        state.loading    = false;
        state.orders     = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchOrders.rejected, (state, action) => {
        if (state.ordersRequestId && action.meta.requestId !== state.ordersRequestId) return;
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── FETCH STATUS COUNTS ── */
      .addCase(fetchStatusCounts.fulfilled, (state, action) => {
        const mode = action.meta.arg?.deliveryMode || "delivery";
        state.statusCounts[mode] = action.payload;
      })

      /* ── FETCH ORDER DETAILS ── */
      .addCase(fetchOrderDetails.pending, (state, action) => {
        state.loading = true;
        state.error   = null;
        state.orderDetailsFor = action.meta.arg;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        // An answer for an order the staff member has already navigated away
        // from must not land on the one they are reading. Acting on another
        // order's items is how the wrong bag goes out of the door.
        if (action.meta.arg !== state.orderDetailsFor) return;
        state.loading      = false;
        state.orderDetails = action.payload;
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        if (action.meta.arg !== state.orderDetailsFor) return;
        state.loading = false;
        state.error   = action.payload;
      })

      /* ── UPDATE ORDER STATUS ── */
      .addCase(updateOrderStatus.pending, (state) => {
        state.updating = true;
        state.error    = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.updating = false;
        /* The move, not the order: { orderNo, from, to }. The row is patched in
           place rather than dropped from the list — the list is not necessarily
           filtered by the status that was left, and the screens re-fetch. */
        const { orderNo, to } = action.payload || {};
        if (!orderNo) return;
        state.orders = state.orders.map((o) =>
          o && o.orderNo === orderNo ? { ...o, status: to } : o
        );
        if (state.orderDetails?.order?.orderNo === orderNo) {
          state.orderDetails.order.status = to;
        }
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.updating = false;
        state.error    = action.payload;
      })

      /* ── FETCH MY ORDERS ── */
      .addCase(fetchMyOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchMyOrders.fulfilled, (state, action) => {
        state.loading  = false;
        state.myOrders = action.payload;
      })
      .addCase(fetchMyOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      })
        .addCase(fetchCustomerOrders.pending, (state) => {
        state.loading = true;
        state.error   = null;
      })
      .addCase(fetchCustomerOrders.fulfilled, (state, action) => {
        state.loading         = false;
        state.customerOrders  = action.payload?.data || [];
        // The customer is answered alongside the history; the list rows no
        // longer carry an embedded user document to read it off.
        state.customerProfile = action.payload?.customer || null;
      })
      .addCase(fetchCustomerOrders.rejected, (state, action) => {
        state.loading = false;
        state.error   = action.payload;
      });
  },
});

export const { clearOrderError, clearOrderDetails } = orderSlice.actions;
export default orderSlice.reducer;

/* ================= SELECTORS ================= */
export const selectAllOrders     = (state) => state.orders.orders       || [];
export const selectMyOrders      = (state) => state.orders.myOrders     || [];
export const selectOrderDetails  = (state) => state.orders.orderDetails;
export const selectPagination    = (state) => state.orders.pagination   || {};
export const selectOrderLoading  = (state) => state.orders.loading;
export const selectOrderUpdating = (state) => state.orders.updating;
export const selectOrderError    = (state) => state.orders.error;
export const selectStatusCounts  = (state) => state.orders.statusCounts || {};
export const selectCustomerOrders = (state) => state.orders.customerOrders || [];
export const selectCustomerProfile = (state) => state.orders.customerProfile;
